/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Modal, Table } from "semantic-ui-react";
import { TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { resourceAdded, resourceUpdated, selectAllResources, selectResourceById, selectResourceCollectionStatus } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import { selectComparativeEvidenceMap } from "../slices/comparativeEvidenceMap/comparativeEvidenceMapSlice";
import { globalLoadingOff, globalLoadingOn } from "../slices/globalLoading/globalLoadingSlice";
import getSearchResults from "../functions/getSearchResultsAPI";
import submitFhirJson from "../functions/submitFhirJsonAPI";
import getFhirJson from "../functions/getFhirJsonAPI";
import jsonEquals from "../functions/jsonEquals";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import GroupResourceEdit from "../resourceComponents/editReferencedResourceComponents/GroupResourceEdit";
import addProfileValuesToNewResource from "../resourceComponents/profileFunctions/addProfileValuesToNewResource";
import createJsonForNewResource from "../resourceComponents/profileFunctions/createJsonForNewResource";
import NewTabLink from "../components/NewTabLink";
import StringEntry from "./StringEntry";
import CodeArrayEntry from "./CodeArrayEntry";
import { convertValueSetToCodeableConceptSet, fevirPublicationStatusActive } from "../dictionaries/THOandFHIRValueSets";
import ProfilesByResourceType from "../dictionaries/ProfilesByResourceType";
import { allFevirResourceTypes } from "../dictionaries/CodeSystemLookup";
import convertEvidenceVariableHandlingToCodeableConcept from "../resourceComponents/resourceDisplayCommonFunctions/convertEvidenceVariableHandlingToCodeableConcept";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "./MarkdownEntry";
import updateElement from "../functions/updateElement";
import { evidenceVariableDefinitionConceptSystemChoices, evidenceVariableDotHandlingValueSet, EvidenceVariableUnacceptableDataHandlingCodeableConceptSet } from "../resourceComponents/EvidenceVariableResourceDisplay";
import CodeableReferenceEntry from "./CodeableReferenceEntry";
import ArrayEntry from "./ArrayEntry";
import CodeableConceptEntry from "./CodeableConceptEntry";
import RelativeTimeEntry from "./RelativeTimeEntry";
import ExpressionEntry from "./ExpressionEntry";
import SEVCO from "../dictionaries/SEVCO";
import { ModelCharacteristicDisplay } from "../resourceComponents/EvidenceResourceDisplay";

const EndpointAnalysisPlanStatisticEntry = ({ elementName, fieldLabel, startingValue,
    setResourceState, startCollapsed, summaryMeasure, containedResourceDictionary = {} }) => {

    const [evidenceStatisticState, setEvidenceStatisticState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingStatistic = {
            description: "", note: [], statisticType: "", category: "", quantity: "", numberOfEvents: "",
            numberAffected: "", sampleSize: "", attributeEstimate: [], modelCharacteristic: []
        }
        if (startingValue) {
            if (startingValue.extension) { startingStatistic.extension = startingValue.extension; }
            if (startingValue.description) { startingStatistic.description = startingValue.description; }
            if (startingValue.note) { startingStatistic.note = startingValue.note; }
            if (startingValue.statisticType) { startingStatistic.statisticType = startingValue.statisticType; }
            if (startingValue.category) { startingStatistic.category = startingValue.category; }
            if (startingValue.quantity) { startingStatistic.quantity = startingValue.quantity; }
            if (startingValue.numberOfEvents || startingValue.numberOfEvents === 0) { startingStatistic.numberOfEvents = startingValue.numberOfEvents; }
            if (startingValue.numberAffected || startingValue.numberAffected === 0) { startingStatistic.numberAffected = startingValue.numberAffected; }
            if (startingValue.sampleSize) {
                startingStatistic.sampleSize = startingValue.sampleSize;
            }
            if (startingValue.attributeEstimate) { startingStatistic.attributeEstimate = startingValue.attributeEstimate; }
            if (startingValue.modelCharacteristic) { startingStatistic.modelCharacteristic = startingValue.modelCharacteristic; }
        } else if (summaryMeasure) {
            startingStatistic.statisticType = summaryMeasure;
        }
        setEvidenceStatisticState(JSON.parse(JSON.stringify(startingStatistic)));
    }, []);

    useEffect((() => {
        if (Object.keys(evidenceStatisticState).length > 0) {
            let newEvidenceStatistic = {};
            if (evidenceStatisticState.extension) { newEvidenceStatistic.extension = evidenceStatisticState.extension; }
            if (evidenceStatisticState.description) { newEvidenceStatistic.description = evidenceStatisticState.description; }
            if (Array.isArray(evidenceStatisticState.note) && evidenceStatisticState.note.length > 0) {
                newEvidenceStatistic.note = evidenceStatisticState.note;
            }
            if (evidenceStatisticState.statisticType && Object.keys(evidenceStatisticState.statisticType).length > 0) {
                newEvidenceStatistic.statisticType = evidenceStatisticState.statisticType;
            }
            if (evidenceStatisticState.category && Object.keys(evidenceStatisticState.category).length > 0) {
                newEvidenceStatistic.category = evidenceStatisticState.category;
            }
            if (evidenceStatisticState.quantity && Object.keys(evidenceStatisticState.quantity).length > 0) {
                newEvidenceStatistic.quantity = evidenceStatisticState.quantity;
            }
            if (evidenceStatisticState.numberOfEvents || evidenceStatisticState.numberOfEvents === 0) {
                newEvidenceStatistic.numberOfEvents = evidenceStatisticState.numberOfEvents;
            }
            if (evidenceStatisticState.numberAffected || evidenceStatisticState.numberAffected === 0) {
                newEvidenceStatistic.numberAffected = evidenceStatisticState.numberAffected;
            }
            if (evidenceStatisticState.sampleSize && Object.keys(evidenceStatisticState.sampleSize).length > 0) {
                newEvidenceStatistic.sampleSize = evidenceStatisticState.sampleSize;
            }
            if (Array.isArray(evidenceStatisticState.attributeEstimate) && evidenceStatisticState.attributeEstimate.length > 0) {
                newEvidenceStatistic.attributeEstimate = evidenceStatisticState.attributeEstimate;
            }
            if (Array.isArray(evidenceStatisticState.modelCharacteristic) && evidenceStatisticState.modelCharacteristic.length > 0) {
                newEvidenceStatistic.modelCharacteristic = evidenceStatisticState.modelCharacteristic;
            }
            if (Object.keys(newEvidenceStatistic).length === 0) {
                newEvidenceStatistic = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newEvidenceStatistic }; });
        }
    }), [evidenceStatisticState]);

    let statisticTypeDisplay = "";
    if (startingValue?.statisticType) {
        if (startingValue.statisticType.coding?.length > 0) {
            if (startingValue.statisticType.coding[0].display) {
                statisticTypeDisplay = startingValue.statisticType.coding[0].display;
            } else if (startingValue.statisticType.coding[0].code) {
                statisticTypeDisplay = startingValue.statisticType.coding[0].code;
            }
        }
        if (startingValue.statisticType.text) {
            statisticTypeDisplay += "   " + startingValue.statisticType.text;
        }
    }
    let statisticValue = "";
    if (startingValue?.quantity) {
        let comparator = "";
        if (startingValue.quantity.comparator) {
            comparator = startingValue.quantity.comparator + " ";
        }
        let unit = "";
        if (startingValue.quantity.unit) {
            unit = " " + startingValue.quantity.unit;
        }
        let value = startingValue.quantity.value;
        if (value && !unit && statisticTypeDisplay.replace("   ", "").toLowerCase() === "percentage") {
            unit = "%";
        }
        statisticValue = comparator + value + unit;
    }
    let sampleSizeQuantified = "";
    if (startingValue?.sampleSize) {
        if (startingValue.sampleSize.numberOfStudies) {
            sampleSizeQuantified = startingValue.sampleSize.numberOfStudies + " studies";
        }
        if (startingValue.sampleSize.numberOfParticipants) {
            if (sampleSizeQuantified) {
                sampleSizeQuantified += ", ";
            }
            sampleSizeQuantified += startingValue.sampleSize.numberOfParticipants + " participants";
        }
        if (startingValue.sampleSize.numberAnalyzed) {
            if (sampleSizeQuantified) {
                sampleSizeQuantified += ", ";
            }
            sampleSizeQuantified += startingValue.sampleSize.numberAnalyzed + " analyzed";
        }
        if (startingValue.sampleSize.knownDataCount) {
            if (sampleSizeQuantified) {
                sampleSizeQuantified += ", ";
            }
            sampleSizeQuantified += startingValue.sampleSize.knownDataCount + " without missing data";
        }
    }

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.category && <span><b>Category:</b> {startingValue.category.text}</span>}
                {startingValue.description && <span>
                    <b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br />
                </span>}
                {Array.isArray(startingValue.note) && startingValue.note.map((note, noteIndex) => { return <span key={noteIndex}><b>Note:</b> {note.text}<br /></span> })}
                <Table style={{ margin: "4px", width: "600px" }}><Table.Body>
                    <Table.Row><Table.Cell style={{ width: "280px", paddingTop: "6px", paddingBottom: "6px", paddingLeft: "12px" }}>
                        <b>{statisticTypeDisplay}</b>
                    </Table.Cell>
                        <Table.Cell style={{ paddingTop: "6px", paddingBottom: "6px", paddingLeft: "12px" }}>
                            <b>{statisticValue}</b>
                        </Table.Cell></Table.Row>
                </Table.Body></Table>
                {!isNaN(startingValue.numberOfEvents) && <span><b>Number of Events: </b>{startingValue.numberOfEvents}<br /></span>}
                {!isNaN(startingValue.numberAffected) && <span><b>Number Affected: </b>{startingValue.numberAffected}<br /></span>}
                {startingValue.sampleSize && <><b>Sample Size: </b>
                    <div style={{ marginLeft: "24px" }}>
                        {sampleSizeQuantified && <><b></b>{sampleSizeQuantified}<br /></>}
                        {startingValue.sampleSize.description && <span><b>Sample Size Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.sampleSize.description} /><br /></span>}
                        {Array.isArray(startingValue.sampleSize.note) && startingValue.sampleSize.note.map((note, noteIndex) => { return <span key={noteIndex}><b>Note:</b> {note.text}<br /></span> })}
                    </div>
                </>}
                {startingValue.attributeEstimate?.length > 0 &&
                    <Table style={{ margin: "4px" }}>
                        <Table.Header><Table.Row>
                            <Table.HeaderCell className={"leftAlignedCell"} style={{ padding: "6px", width: "360px" }}>Attribute</Table.HeaderCell>
                            <Table.HeaderCell style={{ padding: "6px" }}>Value</Table.HeaderCell>
                            <Table.HeaderCell style={{ padding: "6px" }}>Notes</Table.HeaderCell>
                        </Table.Row></Table.Header>
                        <Table.Body>
                            {startingValue.attributeEstimate.map((attributeEstimate, aeIndex) => {
                                let calculatedPercentageAttribute = false;
                                let attribute = "";
                                if (attributeEstimate.level) {
                                    attribute = 100 * attributeEstimate.level + "% ";
                                }
                                if (attributeEstimate.type) {
                                    if (attributeEstimate.type.coding?.length > 0) {
                                        if (attributeEstimate.type.coding[0].display) {
                                            attribute += attributeEstimate.type.coding[0].display;
                                        } else if (attributeEstimate.type.coding[0].code) {
                                            attribute += attributeEstimate.type.coding[0].code;
                                        }
                                    }
                                    if (attributeEstimate.type.text) {
                                        if (attributeEstimate.type.text.toLowerCase() === "percentage") {
                                            calculatedPercentageAttribute = true;
                                        }
                                        attribute += " " + attributeEstimate.type.text;
                                    }
                                }
                                let attributeValue = "";
                                if (attributeEstimate.quantity) {
                                    let comparator = "";
                                    if (attributeEstimate.quantity.comparator) {
                                        comparator = attributeEstimate.quantity.comparator + " ";
                                    }
                                    let unit = "";
                                    if (attributeEstimate.quantity.unit) {
                                        unit = " " + attributeEstimate.quantity.unit;
                                    }
                                    let attributeEstimateValue = "";
                                    if (attributeEstimate.quantity.value !== undefined) {
                                        attributeEstimateValue = attributeEstimate.quantity.value
                                    }
                                    attributeValue = comparator + attributeEstimate.quantity.value + unit;
                                    if (calculatedPercentageAttribute && attributeEstimate.quantity.value && (comparator === "" || comparator === " ") && attributeEstimate.quantity.unit === "%") {
                                        try {
                                            //The plus symbol belongs here, +parseFloat(4).toFixed(2) === 4 as an int vs parseFloat(4).toFixed(2) === "4.00" as a string
                                            attributeEstimateValue = +parseFloat(attributeEstimate.quantity.value).toFixed(2);
                                            if (attributeEstimateValue.toString() !== "NaN") {
                                                attributeValue = attributeEstimateValue + unit;
                                            }
                                        } catch (e) {
                                            console.log(e);
                                        }
                                    }
                                }
                                if (attributeValue === "" && attributeEstimate.range) {
                                    let lowValue = "No Lower Bound";
                                    let highValue = "No Upper Bound";
                                    if (attributeEstimate.range.low?.value !== undefined) {
                                        lowValue = attributeEstimate.range.low.value;
                                        if (attributeEstimate.range.low.unit) {
                                            lowValue += " " + attributeEstimate.range.low.unit;
                                        }
                                    }
                                    if (attributeEstimate.range.high?.value !== undefined) {
                                        highValue = attributeEstimate.range.high.value;
                                        if (attributeEstimate.range.high.unit) {
                                            highValue += " " + attributeEstimate.range.high.unit;
                                        }
                                    }
                                    attributeValue = lowValue + " to " + highValue;
                                }
                                return <Table.Row key={aeIndex}>
                                    <Table.Cell>{attribute}</Table.Cell>
                                    <Table.Cell>{attributeValue}</Table.Cell>
                                    <Table.Cell>
                                        {attributeEstimate.description && <><b>Description: </b><DisplayFromFHIRMarkdown markdown={attributeEstimate.description} /><br /></>}
                                        {attributeEstimate.note && <>{attributeEstimate.note.map((note, noteIndex) => { return <span key={noteIndex}><b>Note:</b> {note.text}<br /></span> })}</>}
                                    </Table.Cell>
                                </Table.Row>
                            }
                            )}
                        </Table.Body>
                    </Table>}
                {(Array.isArray(startingValue.modelCharacteristic) &&
                    startingValue.modelCharacteristic.length > 0) &&
                    startingValue.modelCharacteristic.map((item, itemIndex) => {
                        return <div key={itemIndex}>
                            <p><b>Model Characteristic {itemIndex + 1} of {startingValue.modelCharacteristic.length}</b></p>
                            <div style={{ marginLeft: "24px" }}>
                                <ModelCharacteristicDisplay modelCharactersticInstance={item} />
                            </div>
                        </div>
                    })}
                <br />
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>

                <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                    <span>Provide a human-readable </span>description<span> of the </span>analysis plan for this statistic<span>, and any notes. (optional)</span>
                </div>
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startCollapsed
                    startingValue={evidenceStatisticState.description} setResourceState={setEvidenceStatisticState} />
                <ArrayEntry datatype='Annotation' elementName='note' fieldLabel='Note'
                    startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed
                    startingValue={evidenceStatisticState.note} setResourceState={setEvidenceStatisticState} />
                <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                    <span>Specify the </span>statistic type<span> for the </span>summary measure for this statistic<span>, and any notes.</span>
                </div>
                <CodeableConceptEntry elementName='statisticType' fieldLabel='Statistic Type'
                    startingValue={evidenceStatisticState.statisticType}
                    startCollapsed={!!evidenceStatisticState.statisticType}
                    valueSet={SEVCO.statisticType}
                    codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet(SEVCO.statisticType)}
                    setResourceState={setEvidenceStatisticState} />
                <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                    <span>If statistics will be reported by category, specify the </span>category<span> for this statistic. (optional)</span>
                </div>
                <CodeableConceptEntry elementName='category' fieldLabel='Category'
                    startingValue={evidenceStatisticState.category} startCollapsed
                    setResourceState={setEvidenceStatisticState} />
                <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                    <span>Specify the </span>attribute estimates<span> (e.g., p value, 95% confidence interval, etc.) for the </span>summary measure for this statistic<span>.</span>
                </div>
                <ArrayEntry datatype='EvidenceStatisticAttributeEstimate'
                    elementName='attributeEstimate' fieldLabel='Attribute Estimate'
                    startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed
                    startingValue={evidenceStatisticState.attributeEstimate} setResourceState={setEvidenceStatisticState} />
                <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                    <span>Specify the </span>model characteristics<span> for the </span>summary measure for this statistic<span>.</span>
                </div>
                <ArrayEntry datatype="EvidenceStatisticModelCharacteristic"
                    containedResourceDictionary={containedResourceDictionary}
                    elementName='modelCharacteristic' fieldLabel='Model Characteristic'
                    deletable={true} deletableArray={true} startCollapsed
                    startingValue={evidenceStatisticState.modelCharacteristic}
                    setResourceState={setEvidenceStatisticState}
                    isStatisticModelProfile={true}
                    isEndpointAnalysisPlanProfile={true} />
            </div>
        </>
    }
};

const EvidenceResourceEditForEstimand = ({ foi, summaryMeasure, population, intervention,
    comparator, endpoint, containedResourceDictionary = {} }) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;

    const [editResourceState, setEditResourceState] = useState({});

    useEffect(() => {
        let resourceJsonCopy = JSON.parse(JSON.stringify(resourceJson));
        if (foi.toString() === resourceJsonCopy.id.toString()) {
            if (!(resourceJsonCopy.variableDefinition?.length > 0)) {
                let variableDefinition = [];
                let populationVariable = {
                    "variableRole": "population"
                };
                if (population) {
                    populationVariable.observed = population;
                }
                variableDefinition.push(populationVariable);
                if (intervention && comparator) {
                    let exposureVariable = {
                        "variableRole": "exposure",
                        "comparatorCategory": comparator.display,
                        "observed": {
                            "display": "GroupAssignment: " + intervention.display + " vs. " + comparator.display
                        }
                    };
                    variableDefinition.push(exposureVariable);
                }
                let outcomeVariable = {
                    "variableRole": "outcome"
                };
                if (endpoint) {
                    outcomeVariable.observed = endpoint;
                }
                variableDefinition.push(outcomeVariable);
                resourceJsonCopy.variableDefinition = variableDefinition;
            }
            setEditResourceState(resourceJsonCopy);
        } else {
            alert("caught in the act " + foi + " vs. " + resourceJsonCopy.id + " is " + (foi.toString() === resourceJsonCopy.id.toString()))
        }
    }, [foi, resourceJson]);

    useEffect(() => {
        if (editResourceState && Object.keys(editResourceState).length > 0) {
            if (editResourceState.title !== undefined &&
                parentResourceDetail.revisedResourceJson.title !== editResourceState.title) {
                console.log("resourceUpdate for Evidence.title");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                newResourceDetail.revisedResourceJson.title = editResourceState.title;
                newResourceDetail.displayTitle = editResourceState.title || editResourceState.name;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [editResourceState.title]);

    useEffect(() => {
        if (editResourceState && Object.keys(editResourceState).length > 0) {
            if (editResourceState.name !== undefined &&
                parentResourceDetail.revisedResourceJson.name !== editResourceState.name) {
                console.log("resourceUpdate for Evidence.name");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                newResourceDetail.revisedResourceJson.name = editResourceState.name;
                newResourceDetail.displayTitle = editResourceState.title || editResourceState.name;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [editResourceState.name]);

    useEffect(() => {
        if (editResourceState && Object.keys(editResourceState).length > 0) {
            updateElement(editResourceState, "description", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.description]);

    useEffect(() => {
        if (editResourceState && Object.keys(editResourceState).length > 0) {
            updateElement(editResourceState, "variableDefinition", "ArrayOfEvidenceVariableDefinition", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.variableDefinition]);

    useEffect(() => {
        if (editResourceState && Object.keys(editResourceState).length > 0) {
            updateElement(editResourceState, "statistic", "ArrayOfEvidenceStatistic", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.statistic]);
    //TODO -- add studyType
    return <div style={{ marginLeft: "24px" }}>
        {(editResourceState && Object.keys(editResourceState).length > 0) && <>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                Label the endpoint analysis plan.
            </div>
            <StringEntry elementName='title'
                fieldLabel={'Title (Human-friendly Label for the Evidence Resource)'}
                startingValue={editResourceState.title || ""}
                required={true}
                setResourceState={setEditResourceState} />
            <StringEntry elementName='name' fieldLabel='Name (Machine Label for the Evidence Resource)'
                startingValue={editResourceState.name || ""} setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Provide a human-readable </span>description<span> of the </span>endpoint analysis plan<span>. (optional)</span>
            </div>
            <MarkdownEntry elementName='description' fieldLabel={'Description'}
                startingValue={editResourceState.description || ""}
                setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Adjust the </span>variable definitions<span> (population, exposure/comparison, outcome) as needed. (optional)</span>
            </div>
            <ArrayEntry datatype='EvidenceVariableDefinition' elementName='variableDefinition'
                startCollapsed
                fieldLabel='Variable Definition'
                startingValue={editResourceState.variableDefinition}
                setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>For each statistic, provide a </span>structured endpoint analysis plan<span>. (optional)</span>
            </div>
            <ArrayEntry datatype='EndpointAnalysisPlanStatistic' elementName='statistic'
                fieldLabel='Statistic' startingValue={editResourceState.statistic}
                startEmptyArrayClosed={true} deletable={true}
                deletableArray={true} startCollapsed={true}
                summaryMeasure={summaryMeasure} containedResourceDictionary={containedResourceDictionary}
                setResourceState={setEditResourceState} />

        </>}
    </div>
};

const EvidenceVariableResourceEditForEstimand = ({ foi }) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;

    const [editResourceState, setEditResourceState] = useState({
        name: resourceJson.name,
        title: resourceJson.title,
        shortTitle: resourceJson.shortTitle,
        description: resourceJson.description,
        note: resourceJson.note,
        definition: resourceJson.definition,
        definitionModifier: resourceJson.definitionModifier,
        handling: convertEvidenceVariableHandlingToCodeableConcept(resourceJson.handling),
        category: resourceJson.category,
        conditional: resourceJson.conditional,
        dataStorage: resourceJson.dataStorage,
        timing: resourceJson.timing,
        constraint: resourceJson.constraint,
        missingDataMeaning: resourceJson.missingDataMeaning,
        unacceptableDataHandling: resourceJson.unacceptableDataHandling
    });
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    useEffect(() => {
        if (resourceJson) {
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        if (editResourceState?.title !== undefined &&
            parentResourceDetail.revisedResourceJson.title !== editResourceState.title) {
            console.log("resourceUpdate for EvidenceVariable.title");
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            newResourceDetail.revisedResourceJson.title = editResourceState.title;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.shortTitle || editResourceState.name;
            dispatch(resourceUpdated(newResourceDetail));
        }
    }, [editResourceState.title]);

    useEffect(() => {
        if (editResourceState?.shortTitle !== undefined
            && !parentResourceDetail.revisedResourceJson.shortTitle !== editResourceState.shortTitle) {
            console.log("resourceUpdate for EvidenceVariable.shortTitle");
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            newResourceDetail.revisedResourceJson.shortTitle = editResourceState.shortTitle;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.shortTitle || editResourceState.name;
            dispatch(resourceUpdated(newResourceDetail));
        }
    }, [editResourceState.shortTitle]);

    useEffect(() => {
        if (editResourceState?.name !== undefined
            && parentResourceDetail.revisedResourceJson.name !== editResourceState.name) {
            console.log("resourceUpdate for EvidenceVariable.name");
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            newResourceDetail.revisedResourceJson.name = editResourceState.name;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.shortTitle || editResourceState.name;
            dispatch(resourceUpdated(newResourceDetail));
        }
    }, [editResourceState.name]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "description", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.description]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "definition", "CodeableReference", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.definition]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "definitionModifier", "ArrayOfEvidenceVariableDefinitionModifier", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.definitionModifier]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "handling", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.handling]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "category", "ArrayOfEvidenceVariableCategory", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.category]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "conditional", "Expression", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.conditional]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "dataStorage", "ArrayOfEvidenceVariableDataStorage", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.dataStorage]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "timing", "RelativeTime", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.timing]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "constraint", "ArrayOfEvidenceVariableConstraint", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.constraint]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "missingDataMeaning", "ArrayOfEvidenceVariableMissingDataMeaning", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.missingDataMeaning]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "unacceptableDataHandling", "ArrayOfEvidenceVariableUnacceptableDataHandling", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.unacceptableDataHandling]);

    return <div style={{ marginLeft: "24px" }}>
        {editResourceState && <>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                Label the endpoint.
            </div>
            <StringEntry elementName='title'
                fieldLabel={'Title (Human-friendly Label for Variable Name)'}
                startingValue={editResourceState.title || ""}
                required={true}
                setResourceState={setEditResourceState} />
            <StringEntry elementName='shortTitle'
                fieldLabel={'Short Title (for Variable Name)'}
                startingValue={editResourceState.shortTitle || ""}
                setResourceState={setEditResourceState} />
            <StringEntry elementName='name'
                fieldLabel={'Name (Machine Label for Variable Name)'}
                startingValue={editResourceState.name || ""}
                setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Define the </span>handling<span> of the </span>endpoint<span>.</span>
            </div>
            <CodeableConceptEntry elementName='handling' fieldLabel='Handling'
                startingValue={editResourceState.handling} setResourceState={setEditResourceState}
                codeableConceptLevelValueSet={evidenceVariableDotHandlingValueSet} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>If categorical handling, define the </span>category<span> values. (optional)</span>
            </div>
            <ArrayEntry datatype='EvidenceVariableCategory' elementName='category'
                fieldLabel='Category' startEmptyArrayClosed={true}
                deletable={true} deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.category}
                setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Provide a human-readable </span>description<span> of the </span>endpoint<span>. (optional)</span>
            </div>
            <MarkdownEntry elementName='description' fieldLabel={'Description'}
                startingValue={editResourceState.description || ""}
                setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Provide a structured </span>definition<span> of the </span>endpoint<span>. (optional)</span>
            </div>
            <CodeableReferenceEntry elementName='definition'
                fieldLabel='Variable Definition'
                containedResourceDictionary={containedResourceDictionary}
                referencedResourceTypes={['Group']}
                enableCreation={true} systemChoices={evidenceVariableDefinitionConceptSystemChoices}
                systemChoicesOpen={true}
                startingValue={editResourceState.definition} setResourceState={setEditResourceState} />
            <ArrayEntry datatype='EvidenceVariableDefinitionModifier' elementName='definitionModifier'
                fieldLabel='Definition Modifier' startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.definitionModifier} setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Provide detail for the </span>Timing<span> for the </span>endpoint<span>. (optional)</span>
            </div>
            <RelativeTimeEntry elementName='timing' fieldLabel='Timing'
                startingValue={editResourceState.timing}
                setResourceState={setEditResourceState}
                startCollapsed containedResourceDictionary={containedResourceDictionary} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                    <span>Provide detail for the </span>Conditional<span> requirements for the </span>endpoint<span>, for endpoints only collected under specific conditions. (optional)</span>
                </div>
                <ExpressionEntry elementName='conditional' fieldLabel='Conditional (measured if)'
                    startingValue={editResourceState.conditional}
                    setResourceState={setEditResourceState}
                    startCollapsed />
                <span>Provide detail for </span>Constraints<span> for the </span>endpoint<span>. (optional)</span>
            </div>
            <ArrayEntry datatype='EvidenceVariableConstraint' elementName='constraint'
                fieldLabel='Constraint' startEmptyArrayClosed={true}
                deletable={true} deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.constraint}
                setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Provide detail for </span>Missing data interpretation<span> for the </span>endpoint<span>. (optional)</span>
            </div>
            <ArrayEntry datatype='CodeableConcept' elementName='missingDataMeaning'
                fieldLabel='Missing data' startCollapsed={true}
                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                startingValue={editResourceState.missingDataMeaning}
                setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Provide detail for </span>Unacceptable data handling<span> for the </span>endpoint<span>. (optional)</span>
            </div>
            <ArrayEntry datatype='CodeableConcept' elementName='unacceptableDataHandling'
                fieldLabel='Unacceptable data handling' startEmptyArrayClosed={true}
                deletable={true} deletableArray={true}
                startCollapsed={true}
                startingValue={editResourceState.unacceptableDataHandling}
                codeableConceptLevelValueSet={EvidenceVariableUnacceptableDataHandlingCodeableConceptSet}
                setResourceState={setEditResourceState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Provide detail for </span>Data storage<span> for the </span>endpoint<span>. (optional)</span>
            </div>
            <ArrayEntry datatype='EvidenceVariableDataStorage' elementName='dataStorage'
                fieldLabel='Data storage' startEmptyArrayClosed={true}
                deletable={true} deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.dataStorage}
                setResourceState={setEditResourceState} />
        </>}
    </div>
}

const SelectProfileForNewResource = ({ profileSelectionState, setProfileSelectionState }) => {
    if (!profileSelectionState) {
        console.log("MISSING profileSelectionState");
        return <></>
    } else if (profileSelectionState.profileSelected === "none") {
        return <></>
    } else if (!profileSelectionState.profileChoices) {
        console.log("MISSING profileSelectionState.profileChoices");
        return <></>
    } else if (profileSelectionState.profileChoices.length === 0) {
        console.log("EMPTY profileSelectionState.profileChoices");
        return <></>
    } else {
        return <div>
            {profileSelectionState.profileChoices.length > 0 &&
                <div style={{ marginLeft: "0px", marginBottom: "8px" }}>
                    <br />
                    <b>Select a Profile: </b>
                    &nbsp;
                    <ToggleButtonGroup
                        className="genericToggleButtonGroup buttonGroupWrap medium"
                        exclusive
                        value={profileSelectionState.profileSelected || ""}
                        aria-label='profile-selection'
                        name='profile-selection'
                    >
                        {profileSelectionState.profileChoices.map((profile, profileIndex) => {
                            return <ToggleButton key={profileIndex} className="genericToggleButton" color="primary"
                                aria-label={profile} value={profile}
                                onClick={(e) => {
                                    if (e.target.value) {
                                        let profileName = e.target.value;
                                        setProfileSelectionState(prevState => {
                                            if (profileName === prevState.profileSelected) {
                                                return {
                                                    ...prevState,
                                                    'profileSelected': undefined
                                                }
                                            } else {
                                                return {
                                                    ...prevState,
                                                    'profileSelected': profileName
                                                }
                                            }
                                        });
                                    }
                                }}>{profile}</ToggleButton>
                        })}
                    </ToggleButtonGroup>
                </div>}
        </div>
    }
};

const deliverSearchResults = async (apiToken, modalState, selectedResourceTypes, selectedProfiles, setModalState) => {
    if (modalState.modalOpen && modalState.searchQueryString &&
        modalState.searchQueryCompleted && !modalState.searchResultsDelivered) {
        let searchResults = await getSearchResults(modalState.searchQueryString,
            modalState.searchType,
            selectedResourceTypes,
            selectedProfiles,
            null,
            null,
            null,
            null,
            null,
            null,
            false,
            apiToken);
        setModalState(prevState => {
            return { ...prevState, "searchResults": searchResults, "searchResultsDelivered": true }
        })
    }
};

const SelectResourceFromSearchModal = ({ selectedResourceType, allowedResourceTypes,
    setSelectResourceFromSearchState, selectedProfile, allowedProfiles, setStartCollapsedState }) => {
    const dispatch = useDispatch();
    let startingSelectedResourceTypes = null;
    if (allowedResourceTypes) {
        startingSelectedResourceTypes = allowedResourceTypes;
    }
    if (selectedResourceType) {
        startingSelectedResourceTypes = [selectedResourceType];
    }
    let startingSelectedProfiles = null;
    if (allowedProfiles) {
        startingSelectedProfiles = allowedProfiles;
    }
    if (selectedProfile) {
        startingSelectedProfiles = [selectedProfile];
    }
    let startingModalState = { "modalOpen": true, "searchQueryString": "", "searchType": "Titles", "searchResults": "" };
    const [modalState, setModalState] = useState(startingModalState);
    const [searchResourceTypeLimiterState, setSearchResourceTypeLimiterState] = useState({
        "selectedResourceType": selectedResourceType,//TODO -- this key does not appear to ever get used
        "selectedResourceTypes": startingSelectedResourceTypes
    });
    const [searchProfileLimiterState, setSearchProfileLimiterState] = useState({
        "selectedProfile": selectedProfile,//TODO -- this key does not appear to ever get used
        "selectedProfiles": startingSelectedProfiles
    });
    const apiToken = useSelector(selectCurrentUserApiToken);

    useEffect((() => {
        const doTheSearch = async () => {
            dispatch(globalLoadingOn());
            await deliverSearchResults(apiToken, modalState, searchResourceTypeLimiterState.selectedResourceTypes,
                searchProfileLimiterState.selectedProfiles, setModalState); //TODO - NOW USES THE PLURAL - I don't know which one's the correct one selectedResourceTypes or selectedResourceType and the same for profile
            dispatch(globalLoadingOff());
        }
        doTheSearch();
    }), [modalState])

    const selectTheResource = (data) => {
        setModalState(prevState => { return { ...prevState, modalOpen: false }; });
        if (setStartCollapsedState) {
            setStartCollapsedState(true);
        }
        setSelectResourceFromSearchState(prevState => { return { ...prevState, "selectedResourceReference": data, modalOpen: false }; });
    }

    let modalContent = <div style={{
        paddingTop: "6px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        <Button style={{ padding: "6px", position: "absolute", right: "14px" }} className="formButton negative"
            content="✖"
            onClick={() => {
                setModalState(prevState => { return { ...prevState, modalOpen: false }; });
                setSelectResourceFromSearchState(prevState => { return { ...prevState, modalOpen: false }; });
            }} />
        {(!selectedResourceType) && <>
            <CodeArrayEntry elementName='selectedResourceTypes' fieldLabel='Limit by Resource Type'
                allowedValues={(allowedResourceTypes && allowedResourceTypes.length) ? allowedResourceTypes : allFevirResourceTypes} dataEntryStyle='dropdownsearch'
                startingValue={searchResourceTypeLimiterState.selectedResourceTypes || []}
                setResourceState={setSearchResourceTypeLimiterState} />
            <p style={{ paddingTop: "12px" }}>Limit to resource types of: {(searchResourceTypeLimiterState.selectedResourceTypes &&
                searchResourceTypeLimiterState.selectedResourceTypes.length > 0) ?
                searchResourceTypeLimiterState.selectedResourceTypes.join(', ') : "[No limits]"}</p>
        </>}
        {((selectedResourceType || searchResourceTypeLimiterState?.selectedResourceTypes?.length === 1) &&
            !selectedProfile
            && (allowedProfiles?.length ||
                (ProfilesByResourceType[selectedResourceType || searchResourceTypeLimiterState.selectedResourceTypes?.[0]]
                    && Object.keys(ProfilesByResourceType[selectedResourceType || searchResourceTypeLimiterState.selectedResourceTypes[0]]).length))
        ) && <>
                <br />
                <CodeArrayEntry elementName='selectedProfiles' fieldLabel='Limit by Profile'
                    allowedValues={(allowedProfiles?.length)
                        ?
                        allowedProfiles
                        :
                        ProfilesByResourceType[selectedResourceType ||
                            searchResourceTypeLimiterState.selectedResourceTypes[0]]
                            ?
                            Object.keys(ProfilesByResourceType[selectedResourceType ||
                                searchResourceTypeLimiterState.selectedResourceTypes[0]]
                            )
                            :
                            []}
                    dataEntryStyle='dropdownsearch'
                    startingValue={searchProfileLimiterState.selectedProfiles || []}
                    setResourceState={setSearchProfileLimiterState} />
                <p style={{ paddingTop: "12px" }}>
                    Limit to Profiles of: {(searchProfileLimiterState.selectedProfiles &&
                        searchProfileLimiterState.selectedProfiles.length > 0) ?
                        searchProfileLimiterState.selectedProfiles.join(', ') : "[No limits]"}</p>
                <br />
            </>}
        <h3>Enter search string <Dropdown
            name="searchType"
            placeholder="Search Type"
            closeOnChange selection selectOnBlur={false} autoComplete='off'
            style={{ marginLeft: "8px", fontSize: "14px", fontWeight: "bold", minWidth: "220px" }}
            options={[
                { key: "All Content", text: "All Content", value: "All Content" },
                //{ key: "Content & Element Names", text: "Content & Element Names", value: "Content & Element Names" },
                { key: "Titles", text: "Titles", value: "Titles" },
                /*{ key: "Titles & Descriptsions", text: "Titles & Descriptsions", value: "Titles & Descriptsions" }*/
            ]}
            value={modalState.searchType || ''}
            onChange={(e, data) => {
                setModalState(prevState => { return { ...prevState, "searchType": data.value, "searchQueryCompleted": (!!modalState.searchQueryString?.trim()), "searchResultsDelivered": false } });
            }}
        /></h3>
        <TextField style={{ width: "100%", marginTop: "16px" }}
            className="inputField" type='text' label={"Enter search string"} size="small" variant='outlined'
            value={modalState.searchQueryString || ""}
            onKeyUp={(event) => {
                if (event.key === 'Enter') {
                    setModalState(prevState => { return { ...prevState, "searchQueryCompleted": true, "searchResultsDelivered": false } });
                }
            }}
            onChange={(e) => {
                setModalState(prevState => { return { ...prevState, "searchQueryString": e.target.value } })
            }} />
        {modalState.searchResults && <>
            <h3 style={{ marginTop: "8px", paddingBottom: "4px" }}>Select a resource</h3>
            {modalState.searchResults.length > 0
                ?
                <>
                    {modalState.searchResults.map((result, resultIndex) => {
                        return <p key={resultIndex} style={{ marginBottom: "6px" }}>
                            <span style={{ cursor: "pointer" }}
                                onClick={() => { selectTheResource(result.data); }} ><b>SELECT</b></span>
                            &nbsp;&nbsp;
                            <span >{result.display}</span><span>{result.status === "draft" && <>&nbsp;&nbsp;(draft)</>}</span>
                            &nbsp;&nbsp;
                            <a href={"https://fevir.net" + result.onClick} target='_blank' rel='noopener noreferrer' >View on FEvIR</a>
                        </p>
                    })}
                </>
                :
                <>No results found.</>}
        </>
        }
        <br />
        <br />
    </div>;

    return <Modal
        style={{ padding: "0px", margin: "0px", maxHeight: "92vh", overflowY: "auto" }}
        dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
        open={modalState?.modalOpen}
        centered={false}
        content={modalContent}
    />;
}

const ReferencedResourceContentEntry = ({ referencedResourceJsonState, summaryMeasure,
    population, intervention, comparator, endpoint, containedResourceDictionary = {} }) => {

    const targetResourceDetail = useSelector(state => selectResourceById(state, referencedResourceJsonState.id));
    const targetEditPermission = targetResourceDetail?.permissionData?.editpermission;

    let resourceType = referencedResourceJsonState.resourceType;
    if (!targetResourceDetail) {
        return <p>The resource you are seeking to edit is not loaded. Save your changes and refresh the page to try again.</p>
    }
    if (!targetEditPermission) {
        return <p>You do not have permission for EDITING RESOURCE CONTENT</p>;
    }

    if (resourceType === "Group" || resourceType === "EvidenceVariable" ||
        resourceType === "Evidence") {
        return <div>
            <h4>Change the content for this {resourceType} Resource.</h4>
            <div style={{ marginLeft: "24px", padding: "6px", outline: "2px solid #444444", borderRadius: "8px", backgroundColor: "#FFFFB0" }}>
                {resourceType === "Group" &&
                    <GroupResourceEdit foi={referencedResourceJsonState.id} />}
                {resourceType === "EvidenceVariable" &&
                    <EvidenceVariableResourceEditForEstimand foi={referencedResourceJsonState.id} />}
                {resourceType === "Evidence" &&
                    <EvidenceResourceEditForEstimand foi={referencedResourceJsonState.id}
                        summaryMeasure={summaryMeasure}
                        population={population}
                        intervention={intervention}
                        comparator={comparator}
                        endpoint={endpoint} containedResourceDictionary={containedResourceDictionary} />}
            </div>
            <br />
        </div>
    } else {
        return <p>RESOURCE TYPE NOT RECOGNIZED</p>
    }
};

const createNewReferencedResource = (resourceType, newResourceState, addElementValues) => {
    let newResource = createJsonForNewResource(newResourceState);

    newResource.resourceType = resourceType;
    if (addElementValues) {
        let partOfRelationshipReference;
        for (const key in addElementValues) {
            if (addElementValues[key] || addElementValues[key] === false || addElementValues[key] === 0) {
                if (key === "relatedArtifact" && resourceType === "Composition") {
                    newResource["relatesTo"] = addElementValues[key];
                } else if (key === "partOfRelationshipReference") {
                    partOfRelationshipReference = addElementValues[key];
                } else if (resourceType !== "List") {
                    newResource[key] = addElementValues[key];
                }
            }
        }
        if (partOfRelationshipReference) {
            if (resourceType === "Composition" || resourceType === "Citation" || resourceType === "Evidence" ||
                resourceType === "EvidenceVariable" || resourceType === "ArtifactAssessment" ||
                resourceType === "ResearchStudy"
            ) {
                let partOfRelatesTo = {
                    "type": {
                        "coding": [
                            {
                                "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                                "code": "part-of",
                                "display": "Part Of"
                            }
                        ]
                    },
                    "targetReference": partOfRelationshipReference
                };
                if (newResource.relatesTo && Array.isArray(newResource.relatesTo)) {
                    newResource.relatesTo.push(partOfRelatesTo);
                } else {
                    newResource.relatesTo = [partOfRelatesTo];
                }
            } else if (resourceType === "ActivityDefinition" || resourceType === "PlanDefinition" ||
                resourceType === "Library" || resourceType === "CodeSystem" ||
                resourceType === "ValueSet") {
                let partOfRelatedArtifact = {
                    "type": "part-of",
                    "resourceReference": partOfRelationshipReference
                };
                if (newResource.relatedArtifact && Array.isArray(newResource.relatedArtifact)) {
                    newResource.relatedArtifact.push(partOfRelatedArtifact);
                } else {
                    newResource.relatedArtifact = [partOfRelatedArtifact];
                }
            } else {
                let partOfRelatedArtifactExtension = {
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-relatedArtifact",
                    "valueRelatedArtifact": {
                        "type": "part-of",
                        "resourceReference": partOfRelationshipReference
                    }
                };
                if (newResource.extension && Array.isArray(newResource.extension)) {
                    newResource.extension.push(partOfRelatedArtifactExtension);
                } else {
                    newResource.extension = [partOfRelatedArtifactExtension];
                }
            }
        }
    }

    if (newResource.meta?.profile?.[0] === "http://hl7.org/fhir/uv/ebm/StructureDefinition/dichotomous-intended-outcome" &&
        !newResource.shortTitle) {
        newResource.shortTitle = newResource.title.replace("DichotomousIntendedOutcome: ", "");
    }
    if (newResource.extension) {
        let publicationExtensionFound = false;
        for (let extension of newResource.extension) {
            if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status") {
                extension.valueCodeableConcept = fevirPublicationStatusActive;
                publicationExtensionFound = true;
            }
        }
        if (!publicationExtensionFound) {
            newResource.extension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status",
                "valueCodeableConcept": fevirPublicationStatusActive
            });
        }
    } else {
        newResource.extension = [{
            "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status",
            "valueCodeableConcept": fevirPublicationStatusActive
        }]
    }
    if (resourceType === "Composition" || resourceType === "Citation" || resourceType === "Evidence" ||
        resourceType === "EvidenceVariable" || resourceType === "ResearchStudy" ||
        resourceType === "ActivityDefinition" || resourceType === "PlanDefinition" ||
        resourceType === "Library" || resourceType === "CodeSystem" ||
        resourceType === "ValueSet" || resourceType === "List"
    ) {
        if (!newResource.status) {
            newResource.status = "active";
            if (resourceType === "Composition") {
                newResource.status = "final";
            }
            if (resourceType === "List") {
                newResource.status = "current";
            }
        }
        if (resourceType === "List" && !newResource.mode) {
            newResource.mode = "snapshot";
        }
    }
    return newResource;
};

const DisplayFromFHIRReference = ({ reference }) => {
    if (!reference) {
        return <></>
    }
    if (Array.isArray(reference) || typeof reference !== "object") {
        return <>[ERROR: Reference data not interpretable.]</>;
    }
    let referenceRef = reference.reference;
    if (referenceRef && typeof referenceRef !== "string") {
        return <>[ERROR: Reference.reference data not interpretable.]</>;
    }
    let display = reference.display || referenceRef;
    let targetRef;
    if (referenceRef?.split("/").length === 2) {
        if (referenceRef.split("/")[1] && !isNaN(referenceRef.split("/")[1])) {
            targetRef = "foi-" + referenceRef.split("/")[1];
        }
        referenceRef = "/resources/" + referenceRef;
    } else if (reference.identifier && reference.identifier.system === "https://fevir.net/FLI" && reference.identifier.value) {
        targetRef = "fli-" + reference.identifier.value;
        referenceRef = "/FLI/" + reference.identifier.value;
        if (!reference.display) {
            display = reference.identifier.value;
        }
    }
    if (targetRef) {
        return <span>
            <NewTabLink href={referenceRef} target={targetRef} >{display}</NewTabLink>
        </span>
    }
    if (referenceRef && !referenceRef.startsWith("#")) {
        return <span>
            <a href={referenceRef} target='_blank' rel='noopener noreferrer'>{display}</a>
        </span>
    }
    return <span>{display}</span>
}

const getReferencedResource = async (foi, apiToken, setReferencedResourceJsonState) => {
    let referencedResourceJson = await getFhirJson(foi, null, apiToken, null);
    if (referencedResourceJson) {
        setReferencedResourceJsonState(referencedResourceJson);
    }
};

const ReferenceEntryForEstimand = ({ elementName, fieldLabel, startingValue, setResourceState,
    enableCreation, referencedResourceTypes, startCollapsed, doNotShowFieldLabel,
    startingResourceType, setProfile, selectProfile, selectAnyProfile, addElementValues,
    summaryMeasure, population, intervention, comparator, endpoint,
    containedResourceDictionary }) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const combinedEvidenceMap = useSelector(selectComparativeEvidenceMap);
    const resourceCollection = useSelector(state => selectAllResources(state));
    const resourceCollectionStatus = useSelector(selectResourceCollectionStatus);

    const [referenceState, setReferenceState] = useState({});
    const [startingReferenceState, setStartingReferenceState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed)
    const [selectResourceFromSearchState, setSelectResourceFromSearchState] = useState({});
    const [newResourceFoiState, setNewResourceFoiState] = useState("");
    const [referencedResourceJsonState, setReferencedResourceJsonState] = useState("");
    const [profileSelectionState, setProfileSelectionState] = useState({});
    const [editResourceContentState, setEditResourceContentState] = useState(false);
    const [referencedFoiState, setReferencedFoiState] = useState(null);
    const [newResourceState, setNewResourceState] = useState({});
    const [expandCreateNewResourceState, setExpandCreateNewResourceState] = useState(false);

    useEffect(() => {
        let startingProfileSelectionState = { "profileChoices": [] };
        if (startingResourceType && (setProfile || selectAnyProfile || selectProfile)) {
            const profileDictionary = ProfilesByResourceType[startingResourceType];
            const profileNames = Object.keys(profileDictionary);
            if (profileDictionary) {
                if (setProfile) {
                    if (profileNames.includes(setProfile)) {
                        let meta;
                        let section;
                        let content;
                        let profileUrl = "";
                        let citedArtifactClassificationToAdd;
                        let typeValue;
                        let categoryToAdd;
                        let useContextToAdd;
                        let membershipValue;
                        let actualValue;
                        let handlingValue;
                        let definitionValue;
                        let synthesisTypeValue;
                        let combinationMethodValue;
                        let characteristic;
                        let codeValue;
                        let additionalProfiles;
                        if (profileDictionary[setProfile]) {
                            profileUrl = profileDictionary[setProfile].url;
                            if (profileDictionary[setProfile].citedArtifactClassificationToAdd) {
                                citedArtifactClassificationToAdd = profileDictionary[setProfile].citedArtifactClassificationToAdd;
                            }
                            if (profileDictionary[setProfile].typeValue) {
                                typeValue = profileDictionary[setProfile].typeValue;
                            }
                            if (profileDictionary[setProfile].categoryToAdd) {
                                categoryToAdd = profileDictionary[setProfile].categoryToAdd;
                            }
                            if (profileDictionary[setProfile].useContextToAdd) {
                                useContextToAdd = profileDictionary[setProfile].useContextToAdd;
                            }
                            if (profileDictionary[setProfile].membershipValue) {
                                membershipValue = profileDictionary[setProfile].membershipValue;
                            }
                            if (profileDictionary[setProfile].definitionValue) {
                                definitionValue = profileDictionary[setProfile].definitionValue;
                            }
                            if (profileDictionary[setProfile].actualValue === true || profileDictionary[setProfile].actualValue === false) {
                                actualValue = profileDictionary[setProfile].actualValue;
                            }
                            if (profileDictionary[setProfile].handlingValue) {
                                handlingValue = profileDictionary[setProfile].handlingValue;
                            }
                            if (profileDictionary[setProfile].synthesisTypeValue) {
                                synthesisTypeValue = profileDictionary[setProfile].synthesisTypeValue;
                            }
                            if (profileDictionary[setProfile].combinationMethodValue) {
                                combinationMethodValue = profileDictionary[setProfile].combinationMethodValue;
                            }
                            if (profileDictionary[setProfile].codeValue) {
                                codeValue = profileDictionary[setProfile].codeValue;
                            }
                            if (profileDictionary[setProfile].sectionStarter) {
                                section = profileDictionary[setProfile].sectionStarter;
                            }
                            if (profileDictionary[setProfile].contentStarter) {
                                content = profileDictionary[setProfile].contentStarter;
                            }
                            if (profileDictionary[setProfile].characteristicStarter) {
                                characteristic = profileDictionary[setProfile].characteristicStarter;
                            }
                            if (profileDictionary[setProfile].additionalProfiles) {
                                additionalProfiles = profileDictionary[setProfile].additionalProfiles;
                            }
                        }
                        if (profileUrl) {
                            meta = { "profile": [profileUrl] };
                            if (additionalProfiles) {
                                meta.profile = meta.profile.concat(additionalProfiles);
                            }
                            startingProfileSelectionState = {
                                "profileChoices": [setProfile], "profileDictionary": profileDictionary,
                                'meta': meta,
                                'citedArtifactClassificationToAdd': citedArtifactClassificationToAdd,
                                'section': section,
                                'content': content,
                                'characteristic': characteristic,
                                'typeValue': typeValue,
                                'categoryToAdd': categoryToAdd,
                                'useContextToAdd': useContextToAdd,
                                'membershipValue': membershipValue,
                                'definitionValue': definitionValue,
                                'actualValue': actualValue,
                                'handlingValue': handlingValue,
                                'synthesisTypeValue': synthesisTypeValue,
                                'combinationMethodValue': combinationMethodValue,
                                'codeValue': codeValue,
                                "profileSelected": setProfile
                            };
                        }
                    }
                } else {
                    let profileChoices = [];
                    if (selectAnyProfile) {
                        profileChoices = profileNames;
                    }
                    if (selectProfile) {
                        for (const item of selectProfile) {
                            if (profileNames.includes(item)) {
                                profileChoices.push(item);
                            }
                        }
                    }
                    if (profileChoices.length === 0) {
                        startingProfileSelectionState = { "profileSelected": "none" };
                    } else if (profileChoices.length === 1) {
                        startingProfileSelectionState = {
                            "profileChoices": profileChoices, "profileDictionary": profileDictionary,
                            "profileSelected": profileChoices[0]
                        };
                    } else if (profileChoices.length > 1) {
                        startingProfileSelectionState = { "profileChoices": profileChoices, "profileDictionary": profileDictionary };
                    }
                }
            }
        }
        setProfileSelectionState(startingProfileSelectionState);
    }, []);

    useEffect(() => {
        //console.log("ReferenceEntry useEffect due to change in startingValue")
        let startingReference = { "reference": "", "type": "", "identifier": "", "display": "" };
        if (startingValue) {
            if (startingValue.extension) { startingReference.extension = startingValue.extension; }
            if (startingValue.reference) { startingReference.reference = startingValue.reference; }
            if (startingValue.type) { startingReference.type = startingValue.type; }
            if (startingValue.identifier) { startingReference.identifier = startingValue.identifier; }
            if (startingValue.display) { startingReference.display = startingValue.display; }
            if (!startingReference.display && addElementValues) {
                startingReference.display = addElementValues.title || addElementValues.name || "";
            }
        }
        setReferenceState(JSON.parse(JSON.stringify(startingReference)));
        setStartingReferenceState(JSON.parse(JSON.stringify(startingReference)));
    }, [startingValue]);

    useEffect(() => {
        if (newResourceFoiState) {
            setReferencedFoiState(newResourceFoiState);
        }
    }, [newResourceFoiState]);

    useEffect(() => {
        let resourceType = startingResourceType || referenceState.type;
        if (resourceType && profileSelectionState.profileSelected) {
            addProfileValuesToNewResource(resourceType, profileSelectionState.profileSelected, setNewResourceState, combinedEvidenceMap);
        }
    }, [profileSelectionState.profileSelected]);

    useEffect(() => {
        if (referencedFoiState && resourceCollectionStatus === "succeeded") {
            let referencedResourceJson;
            for (let entry of resourceCollection) {
                if (entry.id === referencedFoiState) {
                    referencedResourceJson = entry.revisedResourceJson;
                    break;
                }
            }
            if (referencedResourceJson) {
                setReferencedResourceJsonState(referencedResourceJson);
            } else {
                getReferencedResource(referencedFoiState, apiToken, setReferencedResourceJsonState);
            }
        }
    }, [referencedFoiState]);

    useEffect((() => {
        //console.log("ReferenceEntry useEffect due to change in referenceState.type")
        if (startingResourceType) {
            if (!referenceState.type) {
                setReferenceState(prevState => { return { ...prevState, 'type': startingResourceType } });
            }
        }
    }), [startingResourceType, referenceState.type]);

    useEffect((() => {
        //console.log("ReferenceEntry useEffect due to change in referenceState.reference")
        if (referenceState.reference?.split("/").length === 2) {
            let presumedFoi = referenceState.reference.split("/")[1];
            if (!isNaN(presumedFoi)) {
                setReferencedFoiState(presumedFoi);
            }
        }
    }), [referenceState.reference]);

    useEffect((() => {
        if (referenceState.type) {
            let profileChoices;
            if (selectAnyProfile || selectProfile) {
                const profileDictionary = ProfilesByResourceType[referenceState.type];
                if (profileDictionary) {
                    profileChoices = [];
                    const profileNames = Object.keys(profileDictionary);
                    if (selectAnyProfile) {
                        profileChoices = profileNames;
                    }
                    if (selectProfile) {
                        for (const item of selectProfile) {
                            if (profileNames.includes(item)) {
                                profileChoices.push(item);
                            }
                        }
                    }
                    if (profileChoices.length === 0) {
                        setProfileSelectionState({ "profileChoices": [], "profileSelected": "none" });
                    } else if (profileChoices.length === 1) {
                        setProfileSelectionState({
                            "profileChoices": profileChoices, "profileDictionary": profileDictionary,
                            "profileSelected": profileChoices[0]
                        });
                    } else if (profileChoices.length > 1) {
                        setProfileSelectionState({ "profileChoices": profileChoices, "profileDictionary": profileDictionary });
                    }
                } else {
                    setProfileSelectionState({ "profileChoices": [], "profileSelected": "none" });
                }
            }
        }
    }), [referenceState.type, selectAnyProfile, selectProfile]);

    useEffect((() => {
        if (selectResourceFromSearchState.selectedResourceReference) {
            setReferenceState(selectResourceFromSearchState.selectedResourceReference);
        }
    }), [selectResourceFromSearchState.selectedResourceReference])

    useEffect((() => {
        //console.log("ReferenceEntry useEffect due to change in referenceState")
        if (Object.keys(referenceState).length &&
            !jsonEquals(startingReferenceState, referenceState) &&
            !(!startingReferenceState.display && !startingReferenceState.reference &&
                !startingReferenceState.identifier && !startingReferenceState.extension &&
                !referenceState.display && !referenceState.reference && !referenceState.identifier &&
                !referenceState.extension
            )
        ) {
            let newReference = {};
            if (referenceState.extension) { newReference.extension = referenceState.extension; }
            if (referenceState.reference) { newReference.reference = referenceState.reference; }
            if (referenceState.type) { newReference.type = referenceState.type; }
            if (referenceState.identifier && Object.keys(referenceState.identifier).length) {
                newReference.identifier = referenceState.identifier;
            }
            if (referenceState.display) {
                newReference.display = referenceState.display;
                setNewResourceState(prevState => { return { ...prevState, "title": referenceState.display }; });
            } else if (addElementValues) {
                newReference.display = addElementValues.title || addElementValues.name || "";
            }
            if (Object.keys(newReference).length === 0 || (!newReference.display && !newReference.reference && !newReference.identifier && !newReference.extension)) {
                newReference = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newReference } });
        }
    }), [referenceState]);

    if (startCollapsedState) {
        return <div>
            {!doNotShowFieldLabel && <b>{fieldLabel}: </b>}
            {startingValue && <DisplayFromFHIRReference reference={startingValue}
                containedResourceJson={startingValue.reference && containedResourceDictionary[startingValue.reference]} />}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
            &nbsp;&nbsp;
            {(enableCreation && (referenceState.reference || referenceState.identifier) &&
                referencedResourceJsonState) &&
                <>
                    <Button className="formButton" style={{ color: "#FFFFFF" }}
                        positive
                        content={`Edit the Resource Content`} disabled={editResourceContentState}
                        onClick={() => {
                            setStartCollapsedState(false);
                            setEditResourceContentState(true);
                        }} />
                    <br /></>
            }
        </div>
    } else if (Object.keys(referenceState).length > 0) {
        return <>
            <div style={{ marginLeft: "24px" }}>
                <p style={{ marginBottom: "2px" }}>Either <b>Search</b> for an existing <b>{fieldLabel}</b>, or <b>Create</b> a new <b>{fieldLabel}</b>.</p>
                <Button className="formButton" style={{ color: "#000000" }}
                    content="Search Resources to Select One"
                    onClick={() => { setSelectResourceFromSearchState({ "modalOpen": true }); }} />
                <br />
                {(editResourceContentState && referencedResourceJsonState.id) &&
                    <ReferencedResourceContentEntry
                        referencedResourceJsonState={referencedResourceJsonState}
                        summaryMeasure={summaryMeasure}
                        population={population}
                        intervention={intervention}
                        comparator={comparator}
                        endpoint={endpoint} containedResourceDictionary={containedResourceDictionary}
                    />}
                {enableCreation &&
                    <>
                        {expandCreateNewResourceState === true ?
                            <>
                                <p style={{ marginBottom: "2px" }}>Provide a name/title for the <b>{fieldLabel}</b>:</p>
                                <StringEntry elementName='display' fieldLabel={'Title of ' + fieldLabel + ' Resource'}
                                    startingValue={referenceState.display || addElementValues?.title}
                                    setResourceState={setReferenceState} />
                                {(profileSelectionState.showProfileSelector &&
                                    profileSelectionState.profileSelected !== "none") &&
                                    <SelectProfileForNewResource profileSelectionState={profileSelectionState}
                                        setProfileSelectionState={setProfileSelectionState} />}
                                {(referenceState.reference || referenceState.identifier) ?
                                    <>
                                        {referencedResourceJsonState &&
                                            <>
                                                <Button className="formButton" style={{ color: "#FFFFFF" }}
                                                    positive={true}
                                                    content={`Edit Resource Content`} disabled={editResourceContentState}
                                                    onClick={() => {
                                                        setEditResourceContentState(true);
                                                    }} />
                                                &nbsp;&nbsp;&nbsp;
                                                {referencedResourceJsonState.id && <span>
                                                    <a href={referencedResourceJsonState.resourceType ? '/resources/' + referencedResourceJsonState.resourceType + "/" + referencedResourceJsonState.id + "/edit" : '/' + referencedResourceJsonState.id}
                                                        target='_blank'
                                                        rel='noopener noreferrer' >Edit this Resource in new window</a>
                                                </span>}
                                                <br />
                                            </>}
                                        {((profileSelectionState.profileChoices?.length || selectAnyProfile || selectProfile) &&
                                            !profileSelectionState.profileSelected && !setProfile &&
                                            profileSelectionState.profileSelected !== "none") ?
                                            <Button className="formButton" style={{ color: "#000000" }}
                                                content={`Select Profile to replace with new ${startingResourceType || referenceState.type} Resource`}
                                                onClick={() => {
                                                    setProfileSelectionState(prevState => {
                                                        return {
                                                            ...prevState,
                                                            showProfileSelector: true,
                                                            profileSelected: ""
                                                        };
                                                    });
                                                }} />
                                            :
                                            <Button className="formButton" style={{ color: "#000000" }}
                                                content={`Replace with new ${startingResourceType || referenceState.type || "[MUST SELECT A TYPE FIRST]"} Resource`}
                                                onClick={async () => {
                                                    let newResource = createNewReferencedResource(startingResourceType ||
                                                        referenceState.type, newResourceState, addElementValues);
                                                    let title = referenceState.display;
                                                    let resourceFOI;
                                                    let response = await submitFhirJson(newResource, title, apiToken);
                                                    if (response?.success) {
                                                        resourceFOI = response.formstateid.toString();
                                                        newResource.id = resourceFOI;
                                                        let resourceBody = {
                                                            id: resourceFOI,
                                                            displayTitle: title,
                                                            resourceType: newResource.resourceType,
                                                            resourceJson: newResource,
                                                            revisedResourceJson: newResource,
                                                            usageData: {
                                                                totalviews: 0,
                                                                totaledits: 1,
                                                                totalusersviewed: 0,
                                                                totalusersedited: 1
                                                            },
                                                            permissionData: {
                                                                editpermission: true,
                                                                readpermission: true,
                                                                adminpermission: true,
                                                                status: "active"
                                                            },
                                                            editors: [{ userid: userId, name: userName }]
                                                        }
                                                        dispatch(resourceAdded(resourceBody));
                                                        setNewResourceFoiState(resourceFOI);
                                                        setReferenceState(prevState => {
                                                            let reference = `${startingResourceType || referenceState.type}/${resourceFOI}`;
                                                            return {
                                                                ...prevState,
                                                                reference: reference,
                                                                type: startingResourceType || referenceState.type
                                                            };
                                                        });
                                                    } else {
                                                        alert("It did not work.");
                                                    }
                                                }} />
                                        }
                                        &nbsp;&nbsp;&nbsp;
                                        {(newResourceFoiState && !referencedResourceJsonState) && <span>
                                            <a href={'https://fevir.net/' + newResourceFoiState}
                                                target='_blank' rel='noopener noreferrer' >Edit this new Resource on FEvIR Platform</a>
                                        </span>}
                                        <br />
                                    </>
                                    :
                                    <>
                                        {((startingResourceType || referenceState.type) &&
                                            (profileSelectionState.profileChoices?.length > 0 || selectAnyProfile || selectProfile) &&
                                            !profileSelectionState.profileSelected &&
                                            !setProfile &&
                                            profileSelectionState.profileSelected !== "none")
                                            ?
                                            <Button className="formButton" style={{ color: "#000000" }}
                                                content={`Select Profile to create this new ${startingResourceType ||
                                                    referenceState.type} Resource`}
                                                onClick={() => {
                                                    setProfileSelectionState(prevState => {
                                                        return {
                                                            ...prevState,
                                                            showProfileSelector: true,
                                                            profileSelected: ""
                                                        };
                                                    });
                                                }} />
                                            :
                                            <Button className="formButton" style={{ color: "#FFFFFF" }}
                                                positive={true}
                                                content={`Create this ${setProfile ||
                                                    profileSelectionState?.profileSelected || startingResourceType ||
                                                    referenceState.type || "[MUST SELECT A TYPE FIRST]"} Resource`}
                                                onClick={async () => {
                                                    let newResource = createNewReferencedResource(startingResourceType ||
                                                        referenceState.type, newResourceState, addElementValues);

                                                    let title = referenceState.display;
                                                    let resourceFOI;
                                                    let response = await submitFhirJson(newResource, title, apiToken);
                                                    if (response?.success) {
                                                        resourceFOI = response.formstateid.toString();
                                                        newResource.id = resourceFOI;
                                                        let resourceBody = {
                                                            id: resourceFOI,
                                                            displayTitle: title,
                                                            resourceType: newResource.resourceType,
                                                            resourceJson: newResource,
                                                            revisedResourceJson: newResource,
                                                            usageData: {
                                                                totalviews: 0,
                                                                totaledits: 1,
                                                                totalusersviewed: 0,
                                                                totalusersedited: 1
                                                            },
                                                            permissionData: {
                                                                editpermission: true,
                                                                readpermission: true,
                                                                adminpermission: true,
                                                                status: "active"
                                                            },
                                                            editors: [{ userid: userId, name: userName }]
                                                        }
                                                        dispatch(resourceAdded(resourceBody));
                                                        setNewResourceFoiState(resourceFOI);
                                                        setReferenceState(prevState => {
                                                            let reference = `${startingResourceType || referenceState.type}/${resourceFOI}`;
                                                            return {
                                                                ...prevState,
                                                                reference: reference,
                                                                type: startingResourceType || referenceState.type
                                                            };
                                                        });
                                                    } else {
                                                        alert("It did not work.");
                                                    }
                                                }} />
                                        }
                                        <br />
                                    </>}
                            </>
                            :
                            <>
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Create New Resource"
                                    onClick={() => { setExpandCreateNewResourceState(true); }} />
                                <br />
                            </>
                        }
                    </>}
            </div>
            {selectResourceFromSearchState.modalOpen &&
                <SelectResourceFromSearchModal selectedResourceType={referenceState.type || startingResourceType}
                    allowedResourceTypes={referencedResourceTypes}
                    selectedProfile={setProfile} allowedProfiles={selectProfile}
                    setSelectResourceFromSearchState={setSelectResourceFromSearchState}
                    setStartCollapsedState={setStartCollapsedState} />}
        </>
    }
}

export default ReferenceEntryForEstimand;
export { EndpointAnalysisPlanStatisticEntry };